import Segment from '@/Segment';

const STORAGE_KEY = 'riseup.cxAgentHasOpenConversation';
// Pre-flag-gating key. Was written for all customers regardless of the
// cxAgentUiChanges flag, so it carries stale pre-flag history — remove it once
// on load to give a clean slate under the new gated key.
const OLD_STORAGE_KEY = 'riseup.intercomHasOpenConversation';

function _readPersistedHasOpenConversation() {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch (_e) {
    return false;
  }
}

function _removeLegacyKey() {
  try {
    localStorage.removeItem(OLD_STORAGE_KEY);
  } catch (_e) {
    // localStorage may be unavailable (private mode, quota) — fail silently
  }
}

_removeLegacyKey();

function _persistHasOpenConversation(value) {
  try {
    if (value) {
      localStorage.setItem(STORAGE_KEY, 'true');
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (_e) {
    // localStorage may be unavailable (private mode, quota) — fail silently
  }
}

const state = {
  unreadMessages: null,
  showOnInit: false,
  isOpen: false,
  // Session-only dismissal (not persisted) so the X can hide the bar even while there are
  // unread messages. Any fresh positive signal (escalation / open / new unread) clears it.
  barDismissed: false,
  hasOpenConversation: _readPersistedHasOpenConversation(),
  // Positive signal captured before feature flags finished loading. Flushed by
  // flushPendingOpenConversation once cxAgentUiChanges is known. Not persisted.
  pendingOpenConversation: false,
};

const getters = {
  shouldShowBar(state, _getters, _rootState, rootGetters) {
    if (!rootGetters['featureFlags/cxAgentUiChanges'] || state.isOpen || state.barDismissed) {
      return false;
    }
    return state.hasOpenConversation || state.unreadMessages > 0;
  },
};

const actions = {
  // Only record (and persist) an open conversation once the cxAgentUiChanges
  // flag is on, so customers without the flag never accumulate the persisted
  // state. Clearing (false) is always honoured — it only removes the key.
  setHasOpenConversation({ commit, rootGetters }, value) {
    if (!value) {
      commit('setHasOpenConversation', false);
      return;
    }
    if (rootGetters['featureFlags/cxAgentUiChanges']) {
      commit('setHasOpenConversation', true);
      return;
    }
    // Flags may not have loaded yet (Intercom boots before featureFlags/init
    // resolves). Capture the signal so it can be flushed once the flag is known.
    commit('setPendingOpenConversation', true);
  },
  flushPendingOpenConversation({ state, commit, rootGetters }) {
    if (state.pendingOpenConversation && rootGetters['featureFlags/cxAgentUiChanges']) {
      commit('setHasOpenConversation', true);
      commit('setPendingOpenConversation', false);
    }
  },
};

const mutations = {
  setUnreadMessages(state, unreadMessages) {
    state.unreadMessages = unreadMessages;
  },
  setShowOnInit(state, showOnInit) {
    state.showOnInit = showOnInit;
  },
  setIsOpen(state, isOpen) {
    state.isOpen = isOpen;
  },
  setBarDismissed(state, barDismissed) {
    state.barDismissed = barDismissed;
  },
  setPendingOpenConversation(state, pendingOpenConversation) {
    state.pendingOpenConversation = pendingOpenConversation;
  },
  setHasOpenConversation(state, hasOpenConversation) {
    const wasOpen = state.hasOpenConversation;
    state.hasOpenConversation = hasOpenConversation;
    _persistHasOpenConversation(hasOpenConversation);
    if (hasOpenConversation) {
      // A fresh positive signal re-surfaces the bar even after a prior dismissal.
      state.barDismissed = false;
    }
    if (hasOpenConversation && !wasOpen) {
      Segment.trackUserGot('IntercomBottomBar_OpenConversationDetected');
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
